// src/services/UsersService.js
import axios from 'axios'

function unwrapList(res) {
  const d = res?.data ?? res
  if (!d) return []
  if (Array.isArray(d)) return d
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(d.rows)) return d.rows
  if (Array.isArray(d.items)) return d.items
  if (d.data && typeof d.data === 'object') {
    const vals = Object.values(d.data).find(v => Array.isArray(v))
    if (vals) return vals
  }
  return []
}

function digitsOnly(v) {
  return String(v ?? '').replace(/\D+/g, '')
}

function str(v) {
  return String(v ?? '').trim()
}

const UsersService = {
  // --- Users CRUD ---
  async list(params = {}) {
    return axios.get('users', { params })
  },

  async get(id, params = {}) {
    return axios.get(`users/${encodeURIComponent(id)}`, { params })
  },

  async create(payload = {}) {
    return axios.post('users', payload)
  },

  async update(id, payload = {}) {
    return axios.put(`users/${encodeURIComponent(id)}`, payload)
  },

  async destroy(id) {
    return axios.delete(`users/${encodeURIComponent(id)}`)
  },

  // ✅ ALIAS: your UserProfile.vue expects this
  // Route: PUT /user/{id}  (per your routes file)
  async updateUserProfile(id, payload = {}) {
    const uid = str(id)
    if (!uid) throw new Error('Missing user id')
    return axios.put(`user/${encodeURIComponent(uid)}`, payload)
  },

  async sendResetLink(id) {
    return axios.post(`users/${encodeURIComponent(id)}/send-reset-link`)
  },

  // Optional but matches your routes: POST users/{id}/status
  async setStatus(id, status) {
    const uid = str(id)
    if (!uid) throw new Error('Missing user id')
    return axios.post(`users/${encodeURIComponent(uid)}/status`, { status })
  },

  async bulk(action, ids = [], payload = {}) {
    return axios.post('users/bulk', { action, ids, ...payload })
  },

  // --- Roles ---
  // NOTE: you said this page only cares about customer roles; keep both endpoints available.
  async roles() {
    // global roles (if any legacy code uses it)
    return axios.get('users/roles')
  },

  async customerRoles() {
    // list of assignable customer roles (read-only)
    // NOTE: your backend route is: GET user/customer-roles
    // If you ever call this, it needs to hit the real route.
    return axios.get('user/customer-roles')
  },

  // --- SIP user syncing (needed by Add User modal) ---
  async listUserSipUsers(userId) {
    return axios.get(`users/${encodeURIComponent(userId)}/sip-users`)
  },

  async syncUserSipUsers(userId, sipUsersOrPayload = []) {
    let sip_users = []

    if (Array.isArray(sipUsersOrPayload)) {
      sip_users = sipUsersOrPayload
    } else if (sipUsersOrPayload && typeof sipUsersOrPayload === 'object') {
      sip_users = Array.isArray(sipUsersOrPayload.sip_users) ? sipUsersOrPayload.sip_users : []
    }

    sip_users = sip_users
      .filter(x => x && (x.sip_user_id || x.UserID))
      .map(x => ({
        sip_user_id: str(x.sip_user_id || x.UserID),
        device_type: x.device_type || 'other',
        is_primary: !!x.is_primary,
        label: x.label ?? null
      }))

    // enforce <= 1 primary
    let primarySeen = false
    sip_users = sip_users.map((x) => {
      if (!x.is_primary) return x
      if (primarySeen) return { ...x, is_primary: false }
      primarySeen = true
      return x
    })

    // if any rows exist and none primary, make first primary
    if (sip_users.length && !sip_users.some(x => x.is_primary)) {
      sip_users[0].is_primary = true
    }

    return axios.post(`users/${encodeURIComponent(userId)}/sip-users/sync`, { sip_users })
  },

  // --- Provisioning ---
  async provisionUser(payload = {}) {
    return axios.post('user-provisioning', payload)
  },

  // --- Rollover Options ---
  async rolloverOptions(params = {}) {
    return axios.get('endpoints/rollover-options', { params })
  },

  // --- Numbers ---
  async numbers(params = {}) {
    return axios.get('numbers', { params })
  },

  // --- Queues ---
  async queues(params = {}) {
    return axios.get('queues', { params })
  },

  // --- Extensions ---
  async extensions(params = {}) {
    return axios.get('extensions', { params })
  },

  // -------------------------
  // ✅ PBX User Profile (NEW ROUTES)
  // Route: GET  /pbx/users/{id}/profile
  // Route: PUT  /pbx/users/{id}/profile
  // -------------------------

  async getPbxProfile(userId, params = {}) {
    const id = str(userId)
    if (!id) throw new Error('Missing userId')
    return axios.get(`pbx/users/${encodeURIComponent(id)}/profile`, { params })
  },

  async updatePbxProfile(userId, payload = {}) {
    const id = str(userId)
    if (!id) throw new Error('Missing userId')
    return axios.put(`pbx/users/${encodeURIComponent(id)}/profile`, payload)
  },

  // ✅ Keep your existing No Answer save call working (UserProfile.vue uses this)
  // We just route it through updatePbxProfile.
  async updatePbxRollover(userId, payload = {}) {
    return UsersService.updatePbxProfile(userId, payload)
  },

  // --- SIP User Details (for password + endpoint id) ---
  async getSipUser(sipUserId) {
    const id = str(sipUserId)
    if (!id) throw new Error('Missing sipUserId')

    try {
      return await axios.get(`sip-users/${encodeURIComponent(id)}`)
    } catch (e1) {
      try {
        return await axios.get(`sipusers/${encodeURIComponent(id)}`)
      } catch (e2) {
        try {
          return await axios.get(`sip-user/${encodeURIComponent(id)}`)
        } catch (e3) {
          throw e1
        }
      }
    }
  },

  // --- Compatibility layer for your modal / page ---
  async callerIdNumbers(params = {}) {
    return UsersService.numbers(params)
  },

  async listQueues(params = {}) {
    return UsersService.queues(params)
  },

  // -------------------------
  // ✅ Local Presence Profiles (LP Profiles)
  // Route: GET    /lp-profiles
  // Route: POST   /lp-profiles
  // Route: GET    /lp-profiles/{id}
  // Route: PUT    /lp-profiles/{id}
  // Route: PATCH  /lp-profiles/{id}
  // Route: DELETE /lp-profiles/{id}
  // -------------------------

  async lpProfiles(params = {}) {
    return axios.get('lp-profiles', { params })
  },

  async getLpProfile(id) {
    const pid = Number(id)
    if (!Number.isFinite(pid) || pid <= 0) throw new Error('Missing/invalid lp profile id')
    return axios.get(`lp-profiles/${encodeURIComponent(String(pid))}`)
  },

  async createLpProfile(payload = {}) {
    return axios.post('lp-profiles', payload)
  },

  async updateLpProfile(id, payload = {}) {
    const pid = Number(id)
    if (!Number.isFinite(pid) || pid <= 0) throw new Error('Missing/invalid lp profile id')
    return axios.put(`lp-profiles/${encodeURIComponent(String(pid))}`, payload)
  },

  async deleteLpProfile(id) {
    const pid = Number(id)
    if (!Number.isFinite(pid) || pid <= 0) throw new Error('Missing/invalid lp profile id')
    return axios.delete(`lp-profiles/${encodeURIComponent(String(pid))}`)
  },


  // helpers
  unwrapList,
  digitsOnly
}

export default UsersService
